import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { OptionsInput } from '@cargos/sprintpay-models';
import { SubTypeKeys } from '@cargos/sprintpay-tools';
import {
    DynamicFieldForm,
    DynamicFieldType,
} from '../../../shared/components/dynamic-field-form/models/dynamic-field-form';
import { BrandingColors } from '../../../shared/models/branding.model';
import { DomainHostingControls } from '../../../shared/models/domain.model';
import { HeaderConfig } from '../../../shared/models/organization-data.model';
import { PaidToFacilityFormControls } from '../../../shared/models/paid-to-facility.model';
import { WorkingScheduleControls } from '../components/organization-details-section-component/models/working-schedule';

export interface AppFeaturesShipmentFieldForm {
    label: FormControl<string | null>;
    placeholder: FormControl<string | null>;
    enabled: FormControl<boolean | null>;
}

export interface AppFeaturesShipmentOrSearchForm {
    dynamicFields: FormArray<FormGroup<DynamicFieldForm>>;
    backgroundType?: FormControl<string | null>;
    backgroundImages?: FormArray<FormGroup<{ name: FormControl<string | null> }>>;
    enableSearchByFlightNumber?: FormControl<boolean | null>;
    backgroundColor?: FormControl<string | null>;
    facilitySource?: FormControl<string | null>;
}

export interface AppFeaturesForm {
    enableDarkMode: FormControl<boolean | null>;
    removeShadow?: FormControl<boolean | null>;
    shipmentForm: FormGroup<AppFeaturesShipmentOrSearchForm>;
    searchForm: FormGroup<AppFeaturesShipmentOrSearchForm>;
    showBreadcrumbs: FormControl<boolean | null>;
    showFooter: FormControl<boolean | null>;
    showFacilityInformation?: FormControl<boolean | null>;
    facilityInformation?: FormArray<FormGroup<FacilityInformationFormControls>>;
    welcomeTitle: FormControl<string | null>;
    welcomeSubtitle: FormControl<string | null>;
    footerLogoHeight: FormControl<string | null>;
    footerLogoWidth: FormControl<string | null>;
    allowSearchForm: FormControl<boolean | null>;
    allowShipmentForm: FormControl<boolean | null>;
    typeFlow: FormControl<string | null>;
    headerConfig: FormGroup<HeaderConfigFormControls>;
    footerCompanyLogo?: FormControl<string | null>;
    footerSections?: FormArray<FormGroup<FooterSectionFormControls>>;
}

export interface AppContactPhoneForm {
    code: FormControl<string | null>;
    phone: FormControl<string | null>;
}

export interface AppForm {
    primaryContactName: FormControl<string | null>;
    contactEmail: FormControl<string | null>;
    phone: FormControl<string | null>;
    organizationName: FormControl<string | null>;
    email: FormControl<string | null>;
    contactPhone: FormGroup<AppContactPhoneForm>;
    alternativeLogoLightUrl: FormControl<string | null>;
    alternativeLogoDarkUrl: FormControl<string | null>;
    alternativeLogoWidth: FormControl<string | null>;
    alternativeLogoHeight: FormControl<string | null>;
    mainLogoLightUrl: FormControl<string | null>;
    mainLogoDarkUrl: FormControl<string | null>;
    mainLogoWidth: FormControl<string | null>;
    mainLogoHeight: FormControl<string | null>;
    setMainLogoToHeader: FormControl<boolean | null>;
    setMainLogoToLogin: FormControl<boolean | null>;
    setAlternativeLogoToHeader: FormControl<boolean | null>;
    setAlternativeLogoToLogin: FormControl<boolean | null>;
    brandingColors: FormControl<BrandingColors>;
    termsUrl: FormControl<string | null>;
    privacyUrl: FormControl<string | null>;
    faqURL: FormControl<string | null>;
    favIconURL: FormControl<string | null>;
    features: FormGroup<AppFeaturesForm>;
    paidToFacilityId: FormControl<string | null>;
    paidToFacilitiesId: FormArray<FormGroup<PaidToFacilityFormControls>>;
    paidToMultipleFacilitiesEnable: FormControl<boolean | null>;
    allowMultiplePayments: FormControl<boolean | null>;
    customerReferenceRequired: FormControl<boolean | null>;
    workingScheduleForm: FormGroup<WorkingScheduleControls>;
    domainHosting: FormGroup<DomainHostingControls>;
}

export enum AppFormMode {
    CREATION = 1,
    EDITION,
}

export enum FaqURLType {
    EMPTY = 1,
    WEBSITE,
    PDF,
}

export interface DynamicFieldPersistent {
    name: string;
    label: string;
    placeholder: string;
    canDisabled: boolean;
    isRequired: boolean;
    fieldType: DynamicFieldType;
    options?: OptionsInput[];
    selectedValidators?: string[];
}

// Header Customization related types

export type HeaderConfigurationFormGroupType = FormGroup<HeaderConfigFormControls>;

export type HeaderConfigFormKeysType = SubTypeKeys<HeaderConfig, string | boolean | undefined | null>;

export type HeaderConfigFormControls = {
    [K in keyof HeaderConfig]: FormControl<HeaderConfig[K]>;
};

export interface FooterSectionFormControls {
    title: FormControl<string>;
    elements: FormArray<FormGroup<FooterElementFormControls>>;
}

export interface FooterElementFormControls {
    type: FormControl<string>;
    message: FormControl<string>;
}

export interface OperatingHourFormControls {
    days: FormControl<string | null>;
    hour: FormControl<string | null>;
}

export interface LatLngFormControls {
    lat: FormControl<number | null>;
    lng: FormControl<number | null>;
}

export interface FacilityInformationFormControls {
    airportName: FormControl<string | null>;
    airportCode: FormControl<string | null>;
    address1: FormControl<string | null>;
    address2: FormControl<string | null>;
    phone: FormControl<string | null>;
    firmsCode: FormControl<string | null>;
    externalFirmsCode: FormControl<string | null>;
    operatingHours: FormArray<FormGroup<OperatingHourFormControls>>;
    position: FormGroup<LatLngFormControls>;
    customers: FormArray<FormControl<string | null>>;
    email: FormArray<FormControl<string | null>>;
    awbPrefixes: FormArray<FormControl<string | null>>;
    city: FormControl<string | null>;
    state: FormControl<string | null>;
    zip: FormControl<string | null>;
    country: FormControl<string | null>;
    amsNominationEmail: FormControl<string | null>;
    workingDays: FormControl<string | null>;
    openTime: FormControl<string | null>;
    closeTime: FormControl<string | null>;
}
